/**
 * Staff sessions store a custom JWT in localStorage (not in the Supabase auth client).
 * Storage / REST calls must send that bearer or RLS rejects tenant-folder uploads.
 */

function isJwt(value: string): boolean {
    return value.split('.').length === 3
}

export function jwtPayload(token: string): Record<string, unknown> | null {
    try {
        const part = token.split('.')[1]
        if (!part) return null
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
        return JSON.parse(atob(padded))
    } catch {
        return null
    }
}

export function isStaffSessionJwt(token: string): boolean {
    const payload = jwtPayload(token)
    if (!payload) return false
    const meta = payload.user_metadata as Record<string, unknown> | undefined
    return meta?.role === 'SuperAdmin' || typeof meta?.staff_username === 'string'
}

export function getStaffSessionBearer(): string {
    if (typeof window === 'undefined') return ''

    const keys = ['staff_session', 'staff_session_shared']
    for (const key of keys) {
        try {
            const raw = sessionStorage.getItem(key) || localStorage.getItem(key)
            if (!raw) continue
            const session = JSON.parse(raw) as Record<string, unknown>
            const token = String(session.access_token || session.refresh_token || '')
                .trim()
                .replace(/^['"]|['"]$/g, '')
            if (token && isJwt(token)) return token
        } catch {
            continue
        }
    }

    return ''
}

function readSupabaseSessionAccessToken(authStorageKey: string): string {
    if (typeof window === 'undefined') return ''
    try {
        const raw = window.localStorage.getItem(authStorageKey)
        if (!raw || raw.length < 80) return ''
        const parsed = JSON.parse(raw) as {
            access_token?: string
            currentSession?: { access_token?: string }
        }
        const token = String(parsed.access_token || parsed.currentSession?.access_token || '')
            .trim()
            .replace(/^['"]|['"]$/g, '')
        return token && isJwt(token) ? token : ''
    } catch {
        return ''
    }
}

/**
 * When staff is logged in without a Supabase GoTrue session, inject the staff JWT
 * so storage RLS sees `authenticated` + `is_super_admin()`.
 */
export function resolveStorageBearer(anonKey: string, authStorageKey: string): string | null {
    const staffBearer = getStaffSessionBearer()
    if (!staffBearer) return null

    const userBearer = readSupabaseSessionAccessToken(authStorageKey)
    const normalizedAnon = anonKey.trim().replace(/^['"]|['"]$/g, '')

    if (!userBearer || userBearer === normalizedAnon) {
        return staffBearer
    }

    return null
}

/** User id for avatar paths when only a staff JWT is present. */
export function getEffectiveAuthUserIdFromStorage(): string | null {
    const staffBearer = getStaffSessionBearer()
    if (staffBearer) {
        const sub = jwtPayload(staffBearer)?.sub
        if (typeof sub === 'string' && sub.length > 0) return sub
    }
    return null
}
